import { FolderOutlined } from '@mui/icons-material';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { PayloadAction } from '@reduxjs/toolkit';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { getMoveToFolders } from '../../../redux/actions/folderActions';
import { AppDispatch } from '../../../redux/store';
import { Folder, FolderResponse } from '../../../Types/folder';
import AutoTruncatingTypographyWithTooltip from '../../AutoTruncatingTypographyWithTooltip';
import DebouncedSearchInput from '../../DebouncedSearchInput';

const MoveToDialog = ({
  folderId,
  folderName,
  onMove,
  isOpen,
  setIsOpen,
}: {
  folderId?: Folder['id'];
  folderName?: Folder['name'];
  onMove: (folderId: Folder['id']) => void;
  isOpen: boolean;
  setIsOpen: (newValue: boolean) => void;
}) => {
  const dispatch = useDispatch<AppDispatch>();

  const [folderSelected, setFolderSelected] = useState<Folder>();
  const [searchQuery, setSearchQuery] = useState('');
  const [pinnedFolders, setPinnedFolders] = useState<Folder[]>([]);
  const [unpinnedFolders, setUnpinnedFolders] = useState<Folder[]>([]);

  const handleMove = useCallback(
    (folderId: Folder['id']) => {
      onMove(folderId);
      setIsOpen(false);
    },
    [onMove, setIsOpen]
  );

  const handleCancel = useCallback(() => {
    setFolderSelected(undefined);
    setSearchQuery('');
    setIsOpen(false);
  }, [setIsOpen]);

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  //Get Folders every time the move to dialog is opened or the search query changes.
  useEffect(() => {
    if (isOpen) {
      dispatch(getMoveToFolders({ searchQuery })).then(
        (action: PayloadAction<FolderResponse | unknown>) => {
          const { pinnedFolders: allPinnedFolders, unpinnedFolders: allUnpinnedFolders } =
            action.payload as FolderResponse;

          setPinnedFolders(
            folderId
              ? allPinnedFolders.filter((tFolder) => tFolder.id !== folderId)
              : allPinnedFolders
          );
          setUnpinnedFolders(
            folderId
              ? allUnpinnedFolders.filter((tFolder) => tFolder.id !== folderId)
              : allUnpinnedFolders
          );
        }
      );
    } else {
      setSearchQuery('');
    }
  }, [isOpen, searchQuery, dispatch, folderId]);

  return (
    <div>
      <Dialog
        open={isOpen}
        onClose={handleCancel}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title" sx={{ borderBottom: '1px solid #C4C4C4' }}>
          Move To
        </DialogTitle>
        <DialogContent sx={{ minHeight: '500px', maxHeight: '500px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
              {folderName && (
                <>
                  <Typography variant="body1">Currently located in: </Typography>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <FolderOutlined sx={{ color: '#757575' }} />
                    <AutoTruncatingTypographyWithTooltip variant="body2" text={folderName} />
                  </div>
                </>
              )}
            </div>
            <div
              style={{
                border: '1px solid black',
                borderRadius: '10px',
                padding: '15px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  borderBottom: '1px solid #C4C4C4',
                  gap: '150px',
                  paddingBottom: '10px',
                }}
              >
                <Typography variant="body1" sx={{ color: '#F900D3' }}>
                  Project Folders
                </Typography>
                <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                  <DebouncedSearchInput value={searchQuery} valueUpdated={handleSearch} />
                </div>
              </div>
              <div>
                {pinnedFolders.map((folder) => (
                  <div
                    key={folder.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 0',
                      cursor: 'pointer',
                      ...(folderSelected?.id === folder.id ? { backgroundColor: '#F6EB21' } : {}),
                    }}
                    onClick={() => setFolderSelected(folder)}
                  >
                    <FolderOutlined sx={{ color: '#F900D3' }} />
                    <AutoTruncatingTypographyWithTooltip variant="body2" text={folder.name} />
                  </div>
                ))}

                {unpinnedFolders.map((folder) => (
                  <div
                    key={folder.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '10px 0',
                      cursor: 'pointer',
                      ...(folderSelected?.id === folder.id ? { backgroundColor: '#F6EB21' } : {}),
                    }}
                    onClick={() => setFolderSelected(folder)}
                  >
                    <FolderOutlined />
                    <Typography variant="body2">{folder.name}</Typography>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} variant="outlined">
            Cancel
          </Button>
          <Button
            disabled={!folderSelected}
            onClick={() => handleMove(folderSelected?.id as string)}
            variant="contained"
            autoFocus
          >
            Move
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default MoveToDialog;
